const types = new Set([
    'acidoBasic',
    'precipitation',
    'complexation',
]);
/** A single equilibrium: one formed species, its components and its pK. */
export class Equation {
    #eq;
    /**
     * Validate an equilibrium and take ownership of a copy of it.
     * @param eq - The equilibrium to wrap. It is copied, never referenced.
     */
    constructor(eq) {
        if (typeof eq.formed !== 'string') {
            throw new Error('equation expects a property "formed" that is a string');
        }
        if (typeof eq.pK !== 'number') {
            throw new Error('equation expects a property "pK" that is a number');
        }
        if (!types.has(eq.type))
            throw new Error('Unexpected type');
        if (Object.prototype.toString.call(eq.components) !== '[object Object]') {
            throw new Error('Unexpected components');
        }
        if (Object.keys(eq.components).length === 0) {
            throw new Error('Components is expected to have at least one key');
        }
        this.#eq = copyEquation(eq);
    }
    /**
     * Return `eq` as an Equation, cloning it when it already is one.
     * @param eq - An equation or its plain description.
     * @returns An Equation that no one else holds a reference to.
     */
    static create(eq) {
        return eq instanceof Equation ? eq.clone() : new Equation(eq);
    }
    get pK() {
        return this.#eq.pK;
    }
    get formed() {
        return this.#eq.formed;
    }
    get components() {
        return this.#eq.components;
    }
    get type() {
        return this.#eq.type;
    }
    clone() {
        return new Equation(this.#eq);
    }
    toJSON() {
        return copyEquation(this.#eq);
    }
    /**
     * Get a new representation of the equation that no longer mentions the solvent.
     *
     * The solvent has unit activity, so it cannot appear in a mass action law.
     * When it is a component it is simply dropped; when it is the formed species
     * the equation is inverted, which is how `H2O ⇄ OH- + H+` becomes the
     * definition of `OH-` from `H+`.
     * @param solvent - Label of the solvent.
     * @returns A new equation without the solvent, or a clone when it did not appear.
     */
    withSolvent(solvent) {
        const components = this.#eq.components;
        const compKeys = Object.keys(components);
        if (this.#eq.formed === solvent) {
            const [firstKey, ...rest] = compKeys;
            if (firstKey === undefined) {
                throw new Error('Components is expected to have at least one key');
            }
            const inverted = {};
            for (const key of rest) {
                inverted[key] = -components[key];
            }
            return new Equation({
                formed: firstKey,
                components: inverted,
                pK: -this.#eq.pK,
                type: this.#eq.type,
            });
        }
        if (!compKeys.includes(solvent))
            return this.clone();
        const { [solvent]: _solvent, ...withoutSolvent } = components;
        return new Equation({ ...this.#eq, components: withoutSolvent });
    }
}
function copyEquation(eq) {
    return {
        formed: eq.formed,
        components: { ...eq.components },
        pK: eq.pK,
        type: eq.type,
    };
}
//# sourceMappingURL=Equation.js.map